// Контекст «активного клуба» для раздела «Конфигурация».
//
// Конфигурация всегда привязана к клубу. Для клубного сотрудника клуб берётся
// из JWT на бэкенде (query не нужен). Ролям БЕЗ своего клуба (super_admin и
// кросс-клубные support/manager из Help) клуб нужно выбрать явно — выбранный
// clubId хранится в localStorage и подставляется в запросы как ?clubId.
// Список клубов у manager закрыт бэкендом (@Roles в clubs.controller), поэтому
// ClubSwitcher в этом случае честно падает на ручной ввод UUID.
//
// useConfigQuery() возвращает объект query для api.* (или undefined), а флаг
// needsClub сигнализирует страницам показать приглашение выбрать клуб.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { DEFAULT_CURRENCY_DISPLAY, toCurrencyDisplay, type CurrencyDisplay } from '@billiard/shared'
import { useAuth } from '@/auth/AuthContext'
import { useFetch } from '@/hooks/useApi'
import type { RequestOptions } from '@/api/client'
import type { SettingRow } from '@/lib/config'

/** Ключ настройки: отображение валюты клуба ('symbol' | 'code' | 'short'). */
export const CURRENCY_DISPLAY_KEY = 'currencyDisplay'

/** Минимальный контракт клуба для селектора (GET /v1/clubs). */
export interface ClubSummary {
  id: string
  name: string
}

interface ClubContextValue {
  /** true — это супер-админ (площадка целиком) */
  isSuperAdmin: boolean
  /** true — роль без своего клуба, клуб выбирается вручную */
  isCrossClub: boolean
  /** выбранный clubId (только для кросс-клубных ролей); для сотрудника клуба — null */
  clubId: string | null
  setClubId: (id: string | null) => void
  /** список клубов для селектора (грузится только для super_admin) */
  clubs: ClubSummary[]
  clubsLoading: boolean
  clubsError: string | null
  /**
   * true, если super_admin ещё не выбрал клуб — страницы конфигурации
   * должны показать приглашение, а не пустые таблицы.
   */
  needsClub: boolean
  /** как показывать валюту выбранного клуба (по умолчанию ISO-код BYN) */
  currencyDisplay: CurrencyDisplay
}

const STORAGE_KEY = 'bo.config.clubId'

const ClubContext = createContext<ClubContextValue | null>(null)

function loadStoredClubId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

export function ClubProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { isSuperAdmin, isCrossClub } = useAuth()
  const [clubId, setClubIdState] = useState<string | null>(() =>
    isCrossClub ? loadStoredClubId() : null
  )

  // Список клубов нужен только ролям без своего клуба.
  const clubsState = useFetch<ClubSummary[]>(isCrossClub ? '/v1/clubs' : null)
  const clubs = clubsState.data ?? []

  // Настройки выбранного клуба — нужны для отображения валюты. Роли без своего
  // клуба запрашивают их только когда клуб выбран (иначе бэкенд не знает, чей он).
  const settingsQuery = useMemo(
    () => (isCrossClub && clubId ? { clubId } : undefined),
    [isCrossClub, clubId]
  )
  const canLoadSettings = !isCrossClub || Boolean(clubId)
  const settingsState = useFetch<SettingRow[]>(
    canLoadSettings ? '/v1/settings' : null,
    settingsQuery,
    [isCrossClub, clubId]
  )

  const currencyDisplay = useMemo<CurrencyDisplay>(() => {
    const row = settingsState.data?.find((r) => r.key === CURRENCY_DISPLAY_KEY)
    return row ? toCurrencyDisplay(row.value) : DEFAULT_CURRENCY_DISPLAY
  }, [settingsState.data])

  const setClubId = useCallback((id: string | null) => {
    setClubIdState(id)
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id)
      else localStorage.removeItem(STORAGE_KEY)
    } catch {
      /* localStorage недоступен — игнорируем */
    }
  }, [])

  const value = useMemo<ClubContextValue>(
    () => ({
      isSuperAdmin,
      isCrossClub,
      clubId: isCrossClub ? clubId : null,
      setClubId,
      clubs,
      clubsLoading: clubsState.loading,
      clubsError: clubsState.error,
      needsClub: isCrossClub && !clubId,
      currencyDisplay
    }),
    [
      isSuperAdmin,
      isCrossClub,
      clubId,
      setClubId,
      clubs,
      clubsState.loading,
      clubsState.error,
      currencyDisplay
    ]
  )

  return <ClubContext.Provider value={value}>{children}</ClubContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useClubContext(): ClubContextValue {
  const ctx = useContext(ClubContext)
  if (!ctx) throw new Error('useClubContext должен вызываться внутри <ClubProvider>')
  return ctx
}

/**
 * Query-параметры для запросов конфигурации.
 * Для роли без своего клуба с выбранным клубом → { clubId }, иначе undefined
 * (клубный сотрудник: клуб выводится из токена).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useConfigQuery(): RequestOptions['query'] | undefined {
  const { isCrossClub, clubId } = useClubContext()
  return useMemo(() => (isCrossClub && clubId ? { clubId } : undefined), [isCrossClub, clubId])
}
